<template>
    <ui-modal class="store-licenses">
        <template #header>
            <h3 v-text="texts.title" class="mb-1 text-center" />
            <h5 v-text="texts.subTitle" class="mb-0 text-center" />
        </template>

        <page-preloader v-if="loader" min-height="10rem" />
        <div v-else class="row text-primary px-3">
            <!-- User alias and login -->
            <form
                class="col-md-6 my-3"
                :class="{ 'col-md-12' : !isEdit }"
            >
                <div class="card p-4">
                    <h4 v-text="texts.licenseTitle" />
                    <div v-text="texts.licenseDesc" class="text-sm mb-3" />
                    <div class="row">
                        <the-input
                            required
                            label="Wpisz nazwę"
                            class="col-lg-3 mb-0"
                            :class="[
                                { 'col-lg-6' : isEdit && isApplication },
                                { 'col-lg-12' : isEdit && !isApplication }
                            ]"
                            :text="userAlias"
                            :invalid="invalidUserAlias"
                            :invalid-icon="false"
                            invalid-feedback="Nazwa jest wymagana"
                            @input="userAlias = $event"
                        />
                        <the-input
                            v-if="isApplication"
                            disabled
                            class="col-lg-6 mb-0 mt-3 mt-lg-0"
                            label="Login"
                            :text="login"
                        />
                    </div>
                </div>
            </form>
            <!-- License status -->
            <div v-if="isEdit" class="col-md-6 my-3">
                <div class="card d-flex justify-content-between p-4">
                    <div>
                        <h4 v-text="texts.statusTitle" />
                        <div
                            v-text="texts.statusDesc"
                            class="text-sm mb-3"
                        />
                    </div>
                    <div
                        class="
                            d-flex flex-column flex-sm-row
                            justify-content-between
                            align-items-sm-center
                        "
                    >
                        <span
                            v-if="status"
                            v-text="status.text"
                            class="status"
                            style="width: 8rem"
                            :class="status.class"
                        />
                        <the-btn
                            class="btn-outline-custom mt-3 mt-sm-0"
                            text="Dezaktywuj"
                            @click="goToDeactivate"
                        />
                    </div>
                </div>
            </div>
            <!-- Password -->
            <div v-if="!isEdit || isApplication" class="col-md-6 my-3">
                <div class="card p-4">
                    <h4 v-text="texts.passwordTitle" />
                    <div v-text="texts.passwordDesc" class="text-sm mb-3" />
                    <the-input
                        v-model="password"
                        type="password"
                        class="cux-exclude mb-0"
                        label="Wpisz hasło"
                        :required="!isEdit"
                    />
                    <the-input
                        v-model="repeatPassword"
                        type="password"
                        class="cux-exclude mb-0 mt-3"
                        label="Powtórz hasło"
                        :required="!isEdit"
                    />
                    <div v-if="password && repeatPassword" class="d-flex mt-2">
                        <a-icon class="mr-2" width="16" :icon="`${passwordsMatch ? 'Ok' : 'ErrorCircle'}`" />
                        <span v-text="passwordsMatch ? 'Hasła się zgadzają' : 'Hasła nie są zgodne'" />
                    </div>
                </div>
            </div>
            <!-- Password rules -->
            <div v-if="!isEdit || isApplication" class="col-md-6 my-3">
                <div class="card p-4">
                    <h4 v-text="'Wymagania'" />
                    <div
                        v-text="'Hasło powinno zawierać:'"
                        class="text-sm mb-3"
                    />
                    <ul class="list-unstyled mb-0">
                        <li
                            v-for="(rule, key) in passwordRules"
                            :key="key"
                            class="d-flex align-items-center mb-2"
                        >
                            <a-icon width="16" class="mr-2" :icon="`${rule.valid ? 'Ok' : 'ErrorCircle'}`" />
                            <span v-text="rule.label" />
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <template #footer>
            <the-btn
                class="btn-outline-custom"
                text="Zamknij"
                @click="$emit('close-modal')"
            />
            <the-btn
                class="btn-secondary"
                :text="texts.acceptBtnText"
                :disabled="!canSubmit || loader"
                @click="submit"
            />
        </template>
    </ui-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { DEVICE_NAMES } from '@/utils/constants/drs/store-devices.js'

export default {
    data: () => ({
        loader: false,
        license: null,
        status: null,
        userAlias: '',
        login: '',
        password: '',
        repeatPassword: ''
    }),
    created() {
        this.initLicenseData()
    },
    computed: {
        ...mapGetters('drsStores', { location: 'getFilterLocationData' }),

        modalArgs() {
            return this.$store.getters.modalState.args
        },
        isEdit() {
            return this.modalArgs?.mode === 'edit'
        },
        isApplication() {
            return this.license.device_type === DEVICE_NAMES.APPLICATION
        },
        texts() {
            const licenseDescBase = `Za pomocą nadanej nazwy zidentyfikujesz,
                który pracownik korzysta z tego dostępu do aplikacji mobilnej`

            if (this.isEdit) {
                const isApplication = this.isApplication
                const isCash = this.license.device_type === DEVICE_NAMES.CASH

                return {
                    title: isApplication ? 'Edycja konta' : 'Edycja urządzenia',
                    subTitle: this.license.device_name,
                    licenseTitle: isApplication ? 'Dane konta' : 'Nazwa urządzenia',
                    licenseDesc: isApplication
                        ? licenseDescBase
                        : `Za pomocą nadanej nazwy zidentyfikujesz 
                        ${isCash ? 'kasę, na której' : 'automat RVM, na którym'}
                        zainstalowany jest system kaucyjny.`,
                    statusTitle: isApplication ? 'Status konta' : 'Status urządzenia',
                    statusDesc: isApplication
                        ? `Możesz dezaktywować dostęp użytkownikowi.
                        Pamiętaj, że usuniętego konta nie można aktywować ponownie.`
                        : `Możesz dezaktywować system kaucyjny na tym urządzeniu.
                        Pamiętaj, że usuniętego urządzenia nie można aktywować ponownie.`,
                    passwordTitle: 'Zmień hasło',
                    passwordDesc: 'Podaj nowe hasło, uwzględnij wszystkie wymagania',
                    acceptBtnText: 'Zapisz zmiany'
                }
            } else {
                return {
                    title: 'Dodawanie pracownika',
                    subTitle: this.location.full_name,
                    licenseTitle: 'Nazwa konta',
                    licenseDesc: `${licenseDescBase} (np. Kasjer Adam)`,
                    passwordTitle: 'Ustaw hasło',
                    passwordDesc: 'Pamiętaj, aby uwzględnić wszystkie wymagania',
                    acceptBtnText: 'Dodaj pracownika'
                }
            }
        },
        isUserAliasChanged() {
            const license_user_alias = this.license.device_alias || ''
            return license_user_alias !== this.userAlias
        },
        passwordRules() {
            const pass = this.password || ''
            return {
                length: {
                    label: 'co najmniej 6 znaków',
                    valid: pass.length >= 6
                },
                lowercase: {
                    label: 'co najmniej 1 małą literę',
                    valid: /[a-z]/.test(pass)
                },
                uppercase: {
                    label: 'co najmniej 1 wielką literę',
                    valid: /[A-Z]/.test(pass)
                },
                digit: {
                    label: 'co najmniej 1 cyfrę',
                    valid: /\d/.test(pass)
                },
                special: {
                    label: 'co najmniej 1 znak specjalny (@$!%*?&)',
                    valid: /[@$!%*?&]/.test(pass)
                }
            }
        },
        isPasswordValid() {
            return Object.values(this.passwordRules).every(rule => rule.valid)
        },
        passwordsMatch() {
            return this.password === this.repeatPassword
        },
        canSubmit() {
            const passwordValid = this.isPasswordValid && this.passwordsMatch

            if (this.isUserAliasEmpty) return false

            if (!this.isEdit) {
                // Create mode
                return passwordValid
            }

            // Edit mode
            if (!this.isApplication) {
                // No application case
                return this.isUserAliasChanged
            }

            // If is application, allow if userAlias has changed
            // and password is empty or password is provided and valid
            return (this.isUserAliasChanged && !this.password && this.passwordsMatch) ||
                (this.password && passwordValid)
        },

        isUserAliasEmpty() {
            return !this.userAlias.trim()
        },

        invalidUserAlias() {
            if (this.isEdit) {
                return this.isUserAliasEmpty
            }

            return this.userAlias.length > 0 && this.isUserAliasEmpty
        }
    },
    methods: {
        ...mapActions('drsLicenses', ['fetchDevices']),

        initLicenseData() {
            const { license = {}, status = null } = this.modalArgs || {}

            this.license = license
            this.status = status
            this.userAlias = license.device_alias || ''
            this.login = license.drs_web_user_login || ''
        },
        goToDeactivate() {
            const { drs_web_user_login: login, device_alias: userAlias } = this.license
            this.$store.dispatch('toggleModal', false)
            this.$nextTick(() => {
                this.$store.dispatch('openModal', {
                    componentPath: 'drs/stores/licenses/DeactivateLicense',
                    args: {
                        login,
                        userAlias,
                        isApplication: this.isApplication,
                        locationId: this.location.id,
                        licenseFormModalArgs: this.modalArgs
                    }
                })
            })
        },
        submit() {
            if (this.loader) {
                return
            }

            if (!this.isEdit) {
                this.createApplicationLicense()
            } else {
                this.updateLicense()
            }
        },
        async updateLicense() {
            try {
                this.loader = true
                // update application license data
                if (this.isApplication) {
                    await this.updateApplicationLicense()
                }
                // update device license data
                if (this.isUserAliasChanged) {
                    await this.updateDeviceLicense()
                }
                this.$store.dispatch('addNotification', {
                    type: 'success',
                    content: 'Zmiany zapisano'
                })
            } catch {
                this.$store.dispatch('addNotification', {
                    type: 'danger',
                    content: 'Wystąpił błąd'
                })
            } finally {
                this.fetchDevices(this.location.id)
                this.loader = false
                this.$store.dispatch('toggleModal', false)
            }
        },
        async updateApplicationLicense() {
            const { license, userAlias, location, password } = this
            await this.$store.$api.patch(
                'drs/api/proxy/users/[drs_user_id]/locationUser',
                {
                    location_ids: [location.id],
                    location_user_id: license.drs_web_user_id,
                    user_alias: userAlias,
                    ...(password && { password })
                }
            )
        },
        async updateDeviceLicense() {
            const { license, userAlias } = this
            await this.$store.$api.patch(
                'drs/api/proxy/users/[drs_user_id]/devices',
                {
                    devices: [{
                        id: license.terminal_id,
                        alias: userAlias,
                        mcuid: license.rad_id
                    }]
                }
            )
        },
        async createApplicationLicense() {
            try {
                this.loader = true
                const { data: createdUserData } = await this.$store.$api.post(
                    'drs/api/proxy/users/[drs_user_id]/locationUser',
                    {
                        shop_id: [this.location.id],
                        password: this.password,
                        user_alias: this.userAlias,
                        user_type: 'D'
                    }
                )
                // goToApplicationLicenseSummary
                this.$store.dispatch('toggleModal', false)
                this.$nextTick(() => {
                    this.goToApplicationLicenseSummary('ok', createdUserData)
                })
            } catch {
                // goToApplicationLicenseSummary
                this.$store.dispatch('toggleModal', false)
                this.$nextTick(() => {
                    this.goToApplicationLicenseSummary('error')
                })
            } finally {
                this.loader = false
            }
        },
        goToApplicationLicenseSummary(code, createdUserData) {
            const isError = code === 'error' || !createdUserData
            const status = !createdUserData ? 'error' : 'ok'
            const details = isError
                ? `W trakcie tworzenia konta wystąpił błąd. Spróbuj ponownie lub wyślij zgłoszenie na
                    <a class="text-primary" href="mailto:pomoc@kaucyjni.pl">pomoc@kaucyjni.pl</a>.`
                : 'Utworzyliśmy dostęp do aplikacji mobilnej. Przekaż pracownikowi poniższe dane:'

            const closeSummaryModal = () => {
                this.fetchDevices(this.location.id)

                if (!isError) {
                    this.$store.dispatch('addNotification', {
                        type: 'success',
                        content: 'Pracownik został dodany'
                    })
                }

                this.$store.dispatch('toggleModal', false)
            }

            const rejectBtn = {
                text: 'Zamknij',
                onRejectBtn: closeSummaryModal
            }

            const acceptBtn = {
                text: isError ? 'Spróbuj ponownie' : 'Dodaj kolejnego pracownika',
                onAcceptBtn: () => {
                    if (!isError) {
                        this.fetchDevices(this.location.id)
                    }
                    this.$store.dispatch('toggleModal', false)
                    this.$nextTick(() => {
                        this.$store.dispatch('openModal', {
                            componentPath: 'drs/stores/licenses/LicenseForm'
                        })
                    })
                }
            }
            const component = isError ? null : 'CreatedUserTable'
            const props = isError
                ? null
                : {
                    userData: {
                        ...createdUserData,
                        password: this.password
                    }
                }

            this.$store.dispatch('openModal', {
                componentPath: 'global/ui/ModalSummary',
                args: {
                    status,
                    details,
                    rejectBtn,
                    acceptBtn,
                    component,
                    props,
                    closeModal: closeSummaryModal
                }
            })
        }
    }
}
</script>
